import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { theme } from '../../theme/theme'

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: ${({ theme }) => theme.transitions.default};
`

const ModalContent = styled.div<{
  $isOpen: boolean
  $maxWidth?: string
  $width?: string
}>`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  width: ${(props) => props.$width || '90%'};
  max-width: ${(props) => props.$maxWidth || '900px'};
  max-height: 90vh;
  overflow: hidden;
  transform: ${(props) => (props.$isOpen ? 'scale(1)' : 'scale(0.9)')};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  flex-direction: column;
`

const ModalHeader = styled.div`
  flex-shrink: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`

const ModalFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  maxWidth?: string
  width?: string
  header?: React.ReactNode
  footer?: React.ReactNode
  children: React.ReactNode
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, maxWidth, width, header, footer, children }) => {
  const [shouldRender, setShouldRender] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      // Trigger animation after render
      setTimeout(() => setIsAnimating(true), 10)
    } else {
      setIsAnimating(false)
      // Remove from DOM after animation completes
      const timeout = setTimeout(() => setShouldRender(false), 200)
      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!shouldRender) return null

  return (
    <ModalOverlay $isOpen={isAnimating} onClick={handleOverlayClick}>
      <ModalContent $isOpen={isOpen} $maxWidth={maxWidth} $width={width}>
        {header && <ModalHeader>{header}</ModalHeader>}
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </ModalOverlay>
  )
}
